import { FC, KeyboardEvent, MouseEvent, ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { Button, ButtonAppearance, Buttons, Icon, Panel, TabbedContainer, useSimpleTabState } from "capitalrx-components-2.0/lib/general";
import * as Color from "capitalrx-components-2.0/lib/general/Color";
import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import classNames from "classnames";

import { createComponentStyleHook } from "../../../tokens";

import tokenStyles from "./styles.module.css";
import legacyStyles from "./styles.module.scss";

export interface TaskSubscription {
  readonly component: FC;
  readonly params?: TaskParams;
}

export interface TaskParams {
  readonly id: string;
  readonly title: ReactNode;
  readonly onClose?: () => void;
}

export interface TaskFlyoutProps {
  readonly subscriptions: ReadonlyArray<TaskSubscription>;
  /**
   * Caps the flyout's height. Accepts a pixel number or any CSS length. Use it to keep the flyout
   * from overlapping a coexisting corner element (such as a floating chatbot): the flyout yields
   * height instead of growing past the cap. When the cap is shorter than the collapsed header, the
   * expand/collapse control is hidden.
   */
  readonly maxHeight?: number | string | null;
  /** Called when the flyout is expanded or collapsed via its header. */
  readonly onExpandedChange?: (expanded: boolean) => void;
  /** Called when the expanded flyout is resized between its small and large sizes. */
  readonly onSizeChange?: (size: "small" | "large") => void;
}

const useStyles = createComponentStyleHook<TaskFlyoutProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "task_flyout",
});

const MinMaxCloseButtonAppearance = {
  ...Buttons.Functional.Tertiary.Large,
  normal: {
    ...Buttons.Functional.Tertiary.Large.normal,
    foregroundColor: Color.LightGrey["300"],
  },
  hover: {
    ...Buttons.Functional.Tertiary.Large.hover,
    foregroundColor: Color.withAlpha(Color.LightGrey["300"], 0.8),
  },
  pressed: {
    ...Buttons.Functional.Tertiary.Large.pressed,
    foregroundColor: Color.withAlpha(Color.LightGrey["300"], 0.5),
  },
} as ButtonAppearance;

const CloseButtonAppearance = {
  ...Buttons.Functional.Tertiary.Compact,
  padding: {
    vertical: 0,
    horizontal: 0,
  },
} as ButtonAppearance;

/* The flyout's natural collapsed height (mirrors `--task_flyout-dimension-collapsed_height`). When a
   `maxHeight` cap squeezes the rendered flyout below this, the expand/collapse control is hidden. */
const COLLAPSED_HEIGHT_PX = 52;

export const TaskFlyout: FC<TaskFlyoutProps> = (props) => {
  const { subscriptions, maxHeight, onExpandedChange, onSizeChange } = props;

  const [expanded, setExpanded] = useState(false);
  const [size, setSize] = useState<"small" | "large">("small");

  const rootRef = useRef<HTMLDivElement>(null);
  const [isBelowCollapsedHeight, setIsBelowCollapsedHeight] = useState(false);

  const hasSubscriptions = subscriptions.length > 0;

  /* Track whether a `maxHeight` cap has squeezed the flyout below its natural collapsed height so the
     header can drop the expand/collapse control. Measured (rather than derived from `maxHeight`) so it
     works whether the cap is a fixed length or a live `calc()`/`min()` expression. */
  useEffect(() => {
    const node = rootRef.current;
    if (!node) return;

    const measureDimensions = () => {
      const height = node.offsetHeight;
      setIsBelowCollapsedHeight(height > 0 && height < COLLAPSED_HEIGHT_PX);
    };

    measureDimensions();
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(measureDimensions);
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasSubscriptions]);

  const [selectedTab, tabChangeHandler] = useSimpleTabState((subscriptions[0]?.params as TaskParams)?.id);

  const { mode, styles: componentStyles, StyleModeBoundary } = useStyles(props);

  const { actualSize, allowResize } = useResponsive({
    actualSize: { desktop: size, mobile: "mobile" },
    allowResize: { desktop: true, mobile: false },
  });

  const headerClickHandler = useCallback(() => {
    const next = !expanded;
    setExpanded(next);
    onExpandedChange?.(next);
  }, [expanded, onExpandedChange]);

  const headerKeyDownHandler = useCallback(
    (ev: KeyboardEvent) => {
      if (ev.key !== "Enter" && ev.key !== " ") return;

      ev.preventDefault();
      const next = !expanded;
      setExpanded(next);
      onExpandedChange?.(next);
    },
    [expanded, onExpandedChange],
  );

  const buttonContainerClick = useCallback((ev: MouseEvent) => {
    ev.stopPropagation();
  }, []);

  const resizeClickHandler = useCallback(() => {
    const next = size === "small" ? "large" : "small";
    setSize(next);
    onSizeChange?.(next);
  }, [size, onSizeChange]);

  const singleTaskCloseFunction = subscriptions.length === 1 ? (subscriptions[0].params as TaskParams | undefined)?.onClose : null;

  const closeClickHandler = useCallback(() => {
    singleTaskCloseFunction?.();
  }, [singleTaskCloseFunction]);

  const rootStyle = maxHeight != null ? { maxHeight: typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight } : undefined;

  if (subscriptions.length === 0) {
    return null;
  }

  let title: ReactNode;
  let contentArea: ReactNode;

  if (subscriptions.length > 1) {
    title = "Tasks";

    contentArea = (
      <div className={classNames(componentStyles.content, componentStyles.multi)}>
        <TabbedContainer selectedTab={selectedTab} onSelectedTabChange={tabChangeHandler}>
          {subscriptions.map((subscription) => {
            const { component: Component, params } = subscription;
            const taskParams = params as TaskParams | undefined;

            if (!taskParams) return false;

            return (
              <TabbedContainer.TabPane
                key={taskParams.id}
                label={
                  <Shelf spacing="small">
                    <div>{taskParams.title}</div>
                    {taskParams.onClose && <Button icon="x" appearance={CloseButtonAppearance} onClick={taskParams.onClose} />}
                  </Shelf>
                }
                id={taskParams.id}
              >
                <div className={componentStyles.taskContainer}>
                  <Component />
                </div>
              </TabbedContainer.TabPane>
            );
          })}
        </TabbedContainer>
      </div>
    );
  } else {
    const { component: Component, params } = subscriptions[0];
    const taskParams = params as TaskParams | undefined;

    if (!taskParams) {
      title = "Invalid Task";

      contentArea = <div className={classNames(componentStyles.content, componentStyles.single)} />;
    } else {
      title = taskParams.title;

      contentArea = (
        <div className={classNames(componentStyles.content, componentStyles.single)}>
          <div className={componentStyles.taskContainer}>
            <Panel>
              <Component />
            </Panel>
          </div>
        </div>
      );
    }
  }

  return (
    <StyleModeBoundary mode={mode}>
      <div
        ref={rootRef}
        style={rootStyle}
        className={classNames(componentStyles.root, componentStyles[actualSize], {
          [componentStyles.expanded]: expanded,
        })}
      >
        <Stack spacing="none">
          <div
            className={componentStyles.header}
            role="button"
            tabIndex={0}
            aria-expanded={expanded}
            onClick={headerClickHandler}
            onKeyDown={headerKeyDownHandler}
          >
            <div className={componentStyles.headerContent}>
              <Shelf alignment="top">
                <div className={componentStyles.headerPrimary}>
                  <Shelf spacing="small">
                    {!isBelowCollapsedHeight && <Icon name={expanded ? "chevron-down" : "chevron-up"} size="regular" color={Color.LightGrey[300]} />}
                    <div>{title}</div>
                    <Shelf.Spacer />
                  </Shelf>
                </div>

                <Shelf.Spacer />

                <Shelf alignment="top" spacing="none">
                  {expanded && allowResize && (
                    <div onClick={buttonContainerClick} onMouseDown={buttonContainerClick}>
                      <Button
                        appearance={MinMaxCloseButtonAppearance}
                        icon={size === "large" ? "arrow-down-right" : "arrow-up-right"}
                        onClick={resizeClickHandler}
                        tooltip={size === "large" ? "Minimize" : "Maximize"}
                      />
                    </div>
                  )}

                  {subscriptions.length === 1 && singleTaskCloseFunction && (
                    <div onClick={buttonContainerClick} onMouseDown={buttonContainerClick}>
                      <Button appearance={MinMaxCloseButtonAppearance} icon="x" onClick={closeClickHandler} tooltip="Close" />
                    </div>
                  )}
                </Shelf>
              </Shelf>
            </div>
          </div>

          {contentArea}
        </Stack>
      </div>
    </StyleModeBoundary>
  );
};
